import React from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Tooltip,
  Collapse,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { BRAND_BLUE } from "../../theme/appTheme";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_BLUE },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND_BLUE },
};

// iOS-style "about to delete" wobble - draws the eye to exactly which
// chip(s) a confirmation dialog is about to remove.
const wiggle = keyframes`
  0% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
  100% { transform: rotate(-3deg); }
`;
const wiggleSx = { animation: `${wiggle} 0.22s ease-in-out infinite`, pointerEvents: "none" };

// Pulsing red glow - highlights the specific entry a dialog is about to
// delete (a whole row doesn't wiggle well like a small chip does).
const deletePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.35); }
  50% { box-shadow: 0 0 0 5px rgba(220, 38, 38, 0); }
`;
const deleteHighlightSx = {
  border: "1px solid #FCA5A5",
  bgcolor: "#FEF2F2",
  animation: `${deletePulse} 1.4s ease-in-out infinite`,
};

function ScopeBadge({ scope, orderCount }) {
  const label = scope === "all" ? "Full access" : `${orderCount} order${orderCount === 1 ? "" : "s"}`;
  const dot = scope === "all" ? BRAND_BLUE : "#9ca3af";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        background: "#EDEDED",
        color: "#4b5563",
        fontSize: "11px",
        fontWeight: 600,
        px: "10px",
        py: "4px",
        borderRadius: "999px",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />
      {label}
    </Box>
  );
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

const SOURCE_LABELS = {
  default: "Default",
  order_created: "From first order",
  admin_added: "Added by admin",
  additional_email: "Additional email",
  self_added: "Self-added",
};

// Fallback for any source value not in SOURCE_LABELS - turns snake_case into
// Title Case rather than showing the raw key with underscores.
function humanizeSource(source) {
  if (!source) return "Unknown";
  return source
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// The access response's own test entries already carry test_identifier -
// prefer it directly. testsById (built by each parent page from data it
// fetches elsewhere) is only a fallback for older/incomplete entries; a
// shortened id is the last resort when neither has a label (e.g.
// CustomerDetail's paginated order list never happened to fetch that order).
function formatTestLabel(test, testsById) {
  const identifier = test.test_identifier || testsById[test.lab_test_id]?.test_identifier;
  return identifier || `Test ${test.lab_test_id.slice(0, 8)}…`;
}

// One order's granted tests, collapsed by default (mirrors CustomerOrderRow's
// expand/collapse convention on the Customer Detail page). The order chip's
// own "x" still removes the whole order; each nested test row has its own
// Access/Emails switches (per-test upsert via onUpdateTestAccess) and its own
// "x" (partial revoke of just that one test).
function OrderAccessGroup({
  order,
  testsById,
  canMutate,
  removingOrderId,
  removingTestKey,
  updatingTestKey,
  onNavigate,
  onRequestRemoveOrder,
  onRequestRemoveTest,
  onRequestToggleTest,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const tests = order.tests || [];
  const isRemovingWholeOrder = removingOrderId === order.id;

  return (
    <Box sx={{ border: "1px solid #f0f0f0", borderRadius: 1, p: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Chip
          size="small"
          label={`#${order.order_number}`}
          onClick={onNavigate}
          onDelete={canMutate && !isRemovingWholeOrder ? onRequestRemoveOrder : undefined}
          disabled={isRemovingWholeOrder}
          sx={{ fontSize: "0.6875rem", height: "24px", cursor: "pointer" }}
        />
        {tests.length > 0 && (
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} sx={{ p: 0.25 }}>
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
            />
          </IconButton>
        )}
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af" }}>
          {tests.length} test{tests.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.75, pl: 1 }}>
          {tests.map((test) => {
            const testKey = `${order.id}:${test.lab_test_id}`;
            const isRemovingTest = removingTestKey === testKey;
            const isUpdatingTest = updatingTestKey === testKey;
            return (
              <Box
                key={test.lab_test_id}
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "#374151", flex: 1, minWidth: 0 }} noWrap>
                  {formatTestLabel(test, testsById)}
                </Typography>
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(test.has_access)}
                      disabled={!canMutate || isUpdatingTest || isRemovingTest}
                      onChange={(e) => onRequestToggleTest(order, test, "hasAccess", e.target.checked)}
                      sx={switchSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.6875rem", color: "#6b7280" }}>Access</Typography>}
                />
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(test.receives_emails)}
                      disabled={!canMutate || isUpdatingTest || isRemovingTest}
                      onChange={(e) => onRequestToggleTest(order, test, "receivesEmails", e.target.checked)}
                      sx={switchSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.6875rem", color: "#6b7280" }}>Emails</Typography>}
                />
                {canMutate && (
                  <IconButton
                    size="small"
                    onClick={() => onRequestRemoveTest(test)}
                    disabled={isRemovingTest}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: "0.9375rem" }} />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

// Only the "off" direction needs a confirmation - turning access/emails on
// is low-risk and applies immediately.
const TOGGLE_OFF_CONFIRM_COPY = {
  // has_access is a grant-level flag, not per-order - turning it off cuts
  // off every order this person can currently see, not just one, so the
  // copy always says "all orders" regardless of scope or order count.
  hasAccess: (user) =>
    `Turn off order access for ${user.email}? They'll immediately lose the ability to view all orders.`,
  receivesEmails: (user) =>
    `Stop order email updates for ${user.email}? All emails to this user will be stopped.`,
};

const TOGGLE_OFF_TITLE = {
  hasAccess: "Turn off access",
  receivesEmails: "Stop email updates",
};

// Per-test toggles confirm BOTH directions (unlike the grant-level switches
// above) - a single test's access/emails is a more surgical, easy-to-miss
// change buried inside a collapsed order group, so every flip gets a
// deliberate confirmation rather than only the "off" direction.
const TEST_TOGGLE_TITLE = {
  hasAccess: { true: "Grant test access", false: "Remove test access" },
  receivesEmails: { true: "Enable test emails", false: "Stop test emails" },
};

const TEST_TOGGLE_CONFIRM_COPY = {
  hasAccess: {
    true: (user, label) => `Give ${user.email} access to test ${label}?`,
    false: (user, label) => `Remove ${user.email}'s access to test ${label}? They'll immediately lose the ability to view it.`,
  },
  receivesEmails: {
    true: (user, label) => `Start sending order emails to ${user.email} for test ${label}?`,
    false: (user, label) => `Stop order emails to ${user.email} for test ${label}?`,
  },
};

// Renders one person's access grant for a customer - shared by CustomerDetail
// and OrderDetail. A "default" row (source: "default", i.e. the customer who
// automatically got access when their order was placed) can't be edited or
// removed here - it isn't a discretionary grant, so its toggles are disabled
// and the overflow menu is hidden. Everyone else (admin-added grants) can be
// toggled off, have a single order unlinked, or be removed entirely, each
// behind a confirmation.
function AccessUserRow({ user, context, onPatch, onRemoveOrder, onRemoveAll, onUpdateTestAccess, testsById = {} }) {
  const navigate = useNavigate();
  const [patchingField, setPatchingField] = React.useState(null);
  const [removingOrderId, setRemovingOrderId] = React.useState(null);
  const [removingTestKey, setRemovingTestKey] = React.useState(null);
  const [updatingTestKey, setUpdatingTestKey] = React.useState(null);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [removeAllOpen, setRemoveAllOpen] = React.useState(false);
  const [removeAllLoading, setRemoveAllLoading] = React.useState(false);
  const [localError, setLocalError] = React.useState("");
  const [pendingToggle, setPendingToggle] = React.useState(null); // { field, value } | null
  const [pendingRemove, setPendingRemove] = React.useState(null); // { order: {id, order_number}, test: {lab_test_id} | null } | null
  const [pendingTestToggle, setPendingTestToggle] = React.useState(null); // { order, test, field, value } | null

  const isDefaultUser = user.source === "default";
  const displayName = user.name || (!user.email_verified ? "Not yet registered" : user.email);
  const orderIds = user.order_ids || [];

  const handleToggle = async (field, value) => {
    setPatchingField(field);
    setLocalError("");
    try {
      await onPatch(user.user_id, { [field]: value });
    } catch (err) {
      setLocalError(err.message || "Couldn't update this.");
    } finally {
      setPatchingField(null);
    }
  };

  // Turning a toggle on applies right away; turning it off is the
  // consequential direction, so confirm it first.
  const requestToggle = (field, value) => {
    if (value) {
      handleToggle(field, value);
    } else {
      setPendingToggle({ field, value });
    }
  };

  const confirmToggle = async () => {
    if (!pendingToggle) return;
    await handleToggle(pendingToggle.field, pendingToggle.value);
    setPendingToggle(null);
  };

  const handleRemoveOrder = async (orderId, testIds) => {
    const testKey = testIds && testIds.length === 1 ? `${orderId}:${testIds[0]}` : null;
    if (testKey) setRemovingTestKey(testKey);
    else setRemovingOrderId(orderId);
    setLocalError("");
    try {
      await onRemoveOrder(user.user_id, orderId, testIds);
    } catch (err) {
      setLocalError(err.message || "Couldn't remove that.");
    } finally {
      if (testKey) setRemovingTestKey(null);
      else setRemovingOrderId(null);
    }
  };

  const requestRemoveOrder = (order) => setPendingRemove({ order, test: null });
  const requestRemoveTest = (order, test) => setPendingRemove({ order, test });

  const confirmRemoveOrder = async () => {
    if (!pendingRemove) return;
    const { order, test } = pendingRemove;
    await handleRemoveOrder(order.id, test ? [test.lab_test_id] : undefined);
    setPendingRemove(null);
  };

  const handleToggleTest = async (order, test, field, value) => {
    const testKey = `${order.id}:${test.lab_test_id}`;
    setUpdatingTestKey(testKey);
    setLocalError("");
    try {
      await onUpdateTestAccess(user.email, order.id, test.lab_test_id, {
        hasAccess: field === "hasAccess" ? value : Boolean(test.has_access),
        receivesEmails: field === "receivesEmails" ? value : Boolean(test.receives_emails),
      });
    } catch (err) {
      setLocalError(err.message || "Couldn't update this test.");
    } finally {
      setUpdatingTestKey(null);
    }
  };

  // Unlike the grant-level requestToggle above, both directions confirm here
  // - see the comment on TEST_TOGGLE_TITLE for why.
  const requestToggleTest = (order, test, field, value) => setPendingTestToggle({ order, test, field, value });

  const confirmToggleTest = async () => {
    if (!pendingTestToggle) return;
    const { order, test, field, value } = pendingTestToggle;
    await handleToggleTest(order, test, field, value);
    setPendingTestToggle(null);
  };

  const handleRemoveAll = async () => {
    setRemoveAllLoading(true);
    setLocalError("");
    try {
      await onRemoveAll(user.user_id);
      setRemoveAllOpen(false);
    } catch (err) {
      setLocalError(err.message || "Couldn't remove access.");
    } finally {
      setRemoveAllLoading(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5, p: 2, bgcolor: "#FFFFFF" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827" }} noWrap>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }} noWrap>
            {user.email}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <ScopeBadge scope={user.scope} orderCount={orderIds.length} />
          {Boolean(onRemoveAll) && !isDefaultUser && (
            <>
              <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                  sx={{ color: "error.main" }}
                  onClick={() => {
                    setMenuAnchor(null);
                    setRemoveAllOpen(true);
                  }}
                >
                  Delete access
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af" }}>
          {SOURCE_LABELS[user.source] || humanizeSource(user.source)}
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af" }}>Linked {formatDate(user.linked_at)}</Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 1 }}>
        <Tooltip title={isDefaultUser ? "Default access from their order can't be changed here" : ""}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={Boolean(user.has_access)}
                disabled={isDefaultUser || Boolean(patchingField)}
                onChange={(e) => requestToggle("hasAccess", e.target.checked)}
                sx={switchSx}
              />
            }
            label={<Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>Access</Typography>}
          />
        </Tooltip>
        <Tooltip title={isDefaultUser ? "Default access from their order can't be changed here" : ""}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={Boolean(user.receives_emails)}
                disabled={isDefaultUser || Boolean(patchingField)}
                onChange={(e) => requestToggle("receivesEmails", e.target.checked)}
                sx={switchSx}
              />
            }
            label={<Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>Emails</Typography>}
          />
        </Tooltip>
      </Box>

      {user.scope === "specific" && orderIds.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5 }}>
          {orderIds.map((o) => (
            <OrderAccessGroup
              key={o.id}
              order={o}
              testsById={testsById}
              canMutate={!isDefaultUser && Boolean(onRemoveOrder)}
              removingOrderId={removingOrderId}
              removingTestKey={removingTestKey}
              updatingTestKey={updatingTestKey}
              onNavigate={() => navigate(`/orders/${o.id}`)}
              onRequestRemoveOrder={() => requestRemoveOrder(o)}
              onRequestRemoveTest={(test) => requestRemoveTest(o, test)}
              onRequestToggleTest={requestToggleTest}
            />
          ))}
        </Box>
      )}

      {localError && (
        <Alert severity="error" sx={{ mt: 1.5, fontSize: "0.75rem", py: 0 }}>
          {localError}
        </Alert>
      )}

      <Dialog open={removeAllOpen} onClose={removeAllLoading ? undefined : () => setRemoveAllOpen(false)}>
        <DialogTitle>Delete access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Their access will be removed from every order they can currently see, they'll stop receiving order
            emails, and this access entry will be permanently deleted - there's no history kept, so re-granting
            access means starting over from an order.
          </DialogContentText>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              p: 1.25,
              borderRadius: 1.5,
              ...deleteHighlightSx,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }} noWrap>
                {displayName}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }} noWrap>
                {user.email}
              </Typography>
            </Box>
            <ScopeBadge scope={user.scope} orderCount={orderIds.length} />
          </Box>

          {orderIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "center", mt: 1.5 }}>
              {orderIds.map((o) => (
                <Chip key={o.id} size="small" label={`#${o.order_number}`} onDelete={() => {}} sx={wiggleSx} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setRemoveAllOpen(false)} disabled={removeAllLoading}>
            Cancel
          </Button>
          <Button onClick={handleRemoveAll} color="error" variant="contained" disabled={removeAllLoading} autoFocus>
            {removeAllLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingRemove)}
        onClose={removingOrderId || removingTestKey ? undefined : () => setPendingRemove(null)}
      >
        <DialogTitle>{pendingRemove?.test ? "Remove this test" : "Remove this order"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingRemove?.test ? (
              <>
                Remove <strong>{user.email}</strong>&apos;s access to just this one test on order{" "}
                <strong>#{pendingRemove.order.order_number}</strong>? Their access to every other test on this order
                stays the same.
              </>
            ) : (
              <>
                Remove <strong>{user.email}</strong>&apos;s access to just order{" "}
                <strong>#{pendingRemove?.order?.order_number}</strong>? Their access to any other orders stays the
                same - only this one is affected.
              </>
            )}
          </DialogContentText>
          {pendingRemove && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Chip
                size="small"
                label={
                  pendingRemove.test
                    ? formatTestLabel(pendingRemove.test, testsById)
                    : `#${pendingRemove.order.order_number}`
                }
                onDelete={() => {}}
                sx={wiggleSx}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setPendingRemove(null)} disabled={Boolean(removingOrderId || removingTestKey)}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveOrder}
            color="error"
            variant="contained"
            disabled={Boolean(removingOrderId || removingTestKey)}
            autoFocus
          >
            {removingOrderId || removingTestKey ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingToggle)} onClose={patchingField ? undefined : () => setPendingToggle(null)}>
        <DialogTitle>{pendingToggle ? TOGGLE_OFF_TITLE[pendingToggle.field] : "Confirm change"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingToggle && TOGGLE_OFF_CONFIRM_COPY[pendingToggle.field](user)}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setPendingToggle(null)} disabled={Boolean(patchingField)}>
            Cancel
          </Button>
          <Button onClick={confirmToggle} color="error" variant="contained" disabled={Boolean(patchingField)} autoFocus>
            {patchingField ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingTestToggle)}
        onClose={updatingTestKey ? undefined : () => setPendingTestToggle(null)}
      >
        <DialogTitle>
          {pendingTestToggle ? TEST_TOGGLE_TITLE[pendingTestToggle.field][String(pendingTestToggle.value)] : "Confirm change"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingTestToggle &&
              TEST_TOGGLE_CONFIRM_COPY[pendingTestToggle.field][String(pendingTestToggle.value)](
                user,
                formatTestLabel(pendingTestToggle.test, testsById)
              )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setPendingTestToggle(null)} disabled={Boolean(updatingTestKey)}>
            Cancel
          </Button>
          <Button
            onClick={confirmToggleTest}
            color={pendingTestToggle?.value ? "primary" : "error"}
            variant="contained"
            disabled={Boolean(updatingTestKey)}
            autoFocus
          >
            {updatingTestKey ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AccessUserRow;
